/* eslint-disable camelcase */
import { useEffect, useCallback, useMemo } from 'react';
import {
  Route,
  Routes,
  Navigate,
  useParams,
  useNavigate,
  useLocation,
  useSearchParams,
} from 'react-router';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { getStatus } from 'redux-resource';
import { DateTime } from 'luxon';
import { toast } from 'react-toastify';
import i18n from 'i18next';

import { coreActions, coreSelectors } from '@gymcloud/core';

import { uiActions } from 'web-actions';
import {
  usePageView,
} from 'web-hooks';

import {
  Button,
  Spinner,
  NotFound,
  ContentWrapper,
  NavigationTabs,
  PersonalWorkout,
} from 'web-components';

import { SVG, Flex, PrintLogo, TextBreadcrumbs } from 'web-styled';
import { getUiState, getFeatures } from 'web-selectors';

import AdditionalPersonalWorkoutPage from './AdditionalPersonalWorkoutPage';

type Props = {
  addtionalType?: 'personalWarmups' | 'personalCooldowns';
};

const PersonalWorkoutsPage = ({ addtionalType }: Props) => {
  const params = useParams();
  const [searchParams] = useSearchParams()
  const navigate = useNavigate();
  const location = useLocation();
  const id = Number(params.id);
  const resourceType = 'personalWorkouts';

  const mapState = useCallback(
    state => ({
      readStatus: getStatus(
        state,
        [
          `${addtionalType || resourceType}.requests.${addtionalType ? 'getFullAdditionalWorkout' : 'getFullWorkout'
          }.status`,
          `${addtionalType || resourceType}.meta[${id}].readStatus`,
        ],
        true,
      ),
      isPro: coreSelectors.getProfile('is_pro')(state),
      userId: coreSelectors.getProfile('id')(state),
      workout: coreSelectors.getResourceById(
        addtionalType || resourceType,
        id,
      )(state),
      workoutEvents: coreSelectors.getResourceMappedList(
        'workoutEvents',
        `personal_workout_${id}`,
      )(state),
      clients: coreSelectors.getResourceMappedList('clients')(state),
      workoutShouldBeSchedule: getUiState('workoutShouldBeSchedule')(state),
      features: getFeatures(state),
    }),
    [id],
  );

  const {
    isPro,
    userId,
    workout,
    workoutEvents,
    clients,
    features,
    readStatus,
    workoutShouldBeSchedule,
  } = useSelector(mapState, shallowEqual);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(
      coreActions.requestFullWorkout({
        id,
        resourceType,
        addtionalType,
      }),
    );
  }, [id, resourceType, addtionalType]);

  useEffect(() => {
    if (addtionalType) return;

    dispatch(
      coreActions.requestLoadPersonalWorkoutEvents({
        id,
        params: {
          scope: 'upcoming',
        },
      }),
    );
  }, [dispatch, id, addtionalType]);

  usePageView({
    eventKey: 'viewed_personal_workout',
    attrs: workout,
    enabled: readStatus.succeeded && workout && searchParams.get('is_new') !== '1',
  });

  const basePath = '/personal_workouts';
  const isEdit = (location.pathname || '').includes('/edit');
  const tabs = useMemo(() => {
    if (addtionalType) return [];

    return [
      {
        name: i18n.t('workout'),
        to: `${basePath}/${id}/${isEdit ? 'edit' : 'overview'}`,
      },
      {
        name: 'Warmup',
        to: `${basePath}/${id}/warmup${isEdit ? '/edit' : ''}`,
      },
      {
        name: 'Cooldown',
        to: `${basePath}/${id}/cooldown${isEdit ? '/edit' : ''}`,
      },
    ];
  }, [isEdit, id, addtionalType]);

  const handleSaveWorkout = () => {
    const onSelect = (values: any, actions: any) => {
      dispatch(
        coreActions.requestSaveAsTemplate({
          id,
          payload: {
            folder_id: values.folderId,
          },
          resourceType,
          callbacks: {
            ...actions,
            onSuccess: () => {
              dispatch(uiActions.calcelMoveEntityModal());
              toast.success('Workout was saved to your library');
            },
          },
        }),
      );
    };

    dispatch(
      uiActions.moveEntityModal({
        modal: {
          title: 'Save to Folder',
          helper: 'Select folder to save Workout template(s) to',
          buttonText: 'Save',
          folderName: 'Workout Templates',
        },
        onSelect,
      }),
    );
  };

  const handleEnter = () => {
    const now = DateTime.local().toISO();
    const onSuccess = (ev: any) => {
      navigate(`/events/${ev.id}/results`);
    };

    dispatch(
      coreActions.requestCreateWorkoutEvents({
        isNow: true,
        userId,
        payload: {
          begins_at: now,
          ends_at: now,
          personal_workout_id: id,
        },
        callbacks: {
          onSuccess,
        },
      }),
    );
  };

  const handleEdit = () => {
    const onConfirm = () => {
      navigate(`/personal_workouts/${workout.id}/edit`);
    };

    dispatch(
      uiActions.confirmModal({
        modal: {
          title: `Edit ${workout?.name}`,
          bodyText: i18n.t('personal_workout.changes.warning'),
          confirmText: 'Update',
        },
        onConfirm,
      }),
    );
  };

  const handleUpdate = (values, actions) => {
    const completeSubmission = () => {
      actions.setSubmitting(false);
      actions.onSuccess?.();
    };

    const finishUpdate = () => {
      if (location.search.includes('schedule') && workoutShouldBeSchedule) {
        const callback = () => {
          completeSubmission();
          dispatch(
            uiActions.setUiState({
              newWorkoutEventModal: {
                isOpen: false,
                startDate: null,
              },
              workoutShouldBeSchedule: null,
            }),
          );
          navigate('/calendar');
        };
        dispatch(
          coreActions.requestCreateWorkoutEvents({
            userId: workout.person_id,
            payload: {
              ...workoutShouldBeSchedule,
              personal_workout_id: id,
            },
            callbacks: {
              onError: callback,
              onSuccess: callback,
            },
          }),
        );
      } else {
        completeSubmission();
        navigate(`/personal_workouts/${id}/overview`);
      }
    };

    const askSyncFutureWorkoutEvents = () => {
      if (!workoutEvents.length) {
        finishUpdate()
        return
      }

      dispatch(
        uiActions.confirmModal({
          modal: {
            title: i18n.t('personal_workout.scheduled_update.title'),
            bodyText: i18n.t('personal_workout.scheduled_update.body'),
            cancelText: i18n.t('personal_workout.scheduled_update.cancel'),
            confirmText: i18n.t('personal_workout.scheduled_update.confirm'),
          },
          onCancel: finishUpdate,
          onConfirm: () => {
            dispatch(
              coreActions.requestSyncFutureWorkoutEvents({
                events: workoutEvents,
                callbacks: {
                  onError: finishUpdate,
                  onSuccess: finishUpdate,
                },
              }),
            );
          },
        }),
      );
    }

    dispatch(
      coreActions.requestUpdateEntity({
        id,
        payload: values,
        endNotify: true,
        resourceType,
        callbacks: {
          onError: () => {
            actions.setSubmitting(false);
            actions.onError?.();
          },
          onSuccess: askSyncFutureWorkoutEvents,
        },
      }),
    );
  };

  const client = useMemo(
    () => clients.find(c => c.id === workout?.person_id),
    [workout?.person_id],
  );

  return (
    <ContentWrapper
      header={(
        <Flex fillSpace align="center" justify="space-between">
          <Flex align="center" spacing={10}>
            <Button
              icon={<SVG.ChevronLeft size={14} />}
              appearance="link"
              onClick={() => {
                navigate(-1);
              }}
              noPrint
            />
            {client && (
              <TextBreadcrumbs
                chevron
                clickable
                onClick={() => {
                  navigate(`/users/${client.id}/workouts`);
                }}
              >
                {`${client.user_profile.first_name || ''} ${client.user_profile.last_name || ''
                  }`}
              </TextBreadcrumbs>
            )}
            {workout && (
              <TextBreadcrumbs active>{workout.name}</TextBreadcrumbs>
            )}
          </Flex>
          <NavigationTabs tabs={tabs} />
          <PrintLogo />
        </Flex>
      )}
    >
      {readStatus.succeeded && (
        <Routes>
          <Route index element={<Navigate to="overview" replace />} />
          <Route
            path="overview"
            element={(
              <PersonalWorkout
                type="workout"
                isPro={isPro}
                readOnly
                workout={workout}
                workoutType="PersonalWorkout"
                resourceType={resourceType!}
                onEdit={handleEdit}
                onSubmit={() => { }}
                onEnterResult={(features?.results
                  && !workout.is_warmup
                  && !workout.is_cooldown
                  ? handleEnter
                  : undefined
                )}
                onSaveToLibrary={isPro ? handleSaveWorkout : undefined}
              />
            )}
          />

          <Route
            path="warmup/*"
            element={(
              <AdditionalPersonalWorkoutPage
                id={workout.warmup_id}
                workout={workout}
                basePath={basePath}
                parentType={resourceType}
                resourceType="warmups"
              />
            )}
          />
          <Route
            path="cooldown/*"
            element={(
              <AdditionalPersonalWorkoutPage
                id={workout.cooldown_id}
                workout={workout}
                basePath={basePath}
                parentType={resourceType}
                resourceType="cooldowns"
              />
            )}
          />

          {isPro && (
            <Route
              path="edit"
              element={(
                <PersonalWorkout
                  type="workout"
                  workout={workout}
                  workoutType="PersonalWorkout"
                  resourceType={resourceType!}
                  onEdit={handleEdit}
                  onSubmit={handleUpdate}
                  onCancel={() => {
                    navigate(`/personal_workouts/${id}/overview`);
                  }}
                  onEnterResult={handleEnter}
                  onSaveToLibrary={handleSaveWorkout}
                />
              )}
            />
          )}
        </Routes>
      )}

      {readStatus.pending && <Spinner message="Loading Personal Workout..." />}

      {readStatus.failed && (
        <NotFound title={`Personal Workout Not Found (${id})`} />
      )}
    </ContentWrapper>
  );
};

export default PersonalWorkoutsPage;
